import type {
  LinkableDlnItemsLookupParams,
  LinkableDlnItemsResponse,
} from './types';

import { ENDPOINT } from '~/constants/endpoints';

import type { KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';

import {
  zodLinkableDlnItemsQueryParamsSchema,
  zodLinkableDlnItemsResponseSchema,
} from './validations';

/**
 * Fetches one page of the delivery note lines that can be linked to a line
 * item.
 *
 * @param params - Invoice, line item, filters and the page window.
 * @param options - Request options supplied by TanStack Query.
 * @returns The page plus the counts over every matching line.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/search_linkable_dln_items_invoices__invoice_id__line_items__line_item_id__linkable_dln_items_get
 */
export async function fetchLinkableDlnItems(
  {
    filters,
    invoiceId,
    lineItemId,
    limit,
    offset,
  }: LinkableDlnItemsLookupParams & { limit: number; offset: number },
  options?: KyRequestOptions,
): Promise<LinkableDlnItemsResponse> {
  return vestigasApi
    .get(ENDPOINT.INVOICE.GET_LINKABLE_DLN_ITEMS(invoiceId, lineItemId), {
      ...options,
      searchParams: { ...filters, limit, offset },
      validate: {
        ...options?.validate,
        isResponseSensitive: true,
        response: zodLinkableDlnItemsResponseSchema,
        searchParams: zodLinkableDlnItemsQueryParamsSchema.transform((data) =>
          withoutObjectKeysWhereValueIs(data, [undefined, '']),
        ),
      },
    })
    .json<LinkableDlnItemsResponse>();
}
